import { deadlineSeverity, formatDuration, type TaskLifecycle } from './taskDeadline.ts'

/**
 * The compute time limit email preference and the in-app notice (nobgp/app#354, the app half of
 * nobgp-router#1552). Pure rules only, so `npm test` can check them.
 *
 * ⚠⚠ THE TWO COLUMNS COME FROM ROUTER MIGRATION 0199, AND BEFORE ITS APPLY THEY DO NOT EXIST.
 * A select that names them fails with 42703. So the preference has its own read (`useDeadlineNotify`)
 * and is NEVER added to the profile select: one unknown column fails the whole query, and the
 * profile would break for every user.
 */

/** The column default (`profiles.compute_deadline_lead_minutes default 60`). */
export const DEFAULT_LEAD_MINUTES = 60
/**
 * The column CHECK range, 15..1440. A value outside it is refused by the database.
 *
 * ⚠⚠ THE FLOOR IS 15 AND NOT 5. `0199`'s check is `between 15 and 1440`, and the floor is tied to
 * the compute-stop sweeper's ten-minute tick: a lead below it can fall between two ticks and the
 * reminder is then never sent. 0199's own header and column COMMENT say 5; the CHECK is what runs.
 */
export const MIN_LEAD_MINUTES = 15
export const MAX_LEAD_MINUTES = 1440

export const isValidLeadMinutes = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= MIN_LEAD_MINUTES && value <= MAX_LEAD_MINUTES

/** The preference as the app holds it. */
export type DeadlinePreference = { enabled: boolean; leadMinutes: number }

/**
 * The `profiles` row of the preference read, made safe.
 *
 * ⚠ Only an explicit `true` turns the email on, and a lead time outside the CHECK range reads as
 * the default. The row is not typed by `supabase.d.ts` until the types are generated after 0199.
 */
export const parseDeadlinePreference = (row: unknown): DeadlinePreference => {
  const r = row && typeof row === 'object' ? (row as Record<string, unknown>) : {}
  return {
    enabled: r.notify_compute_deadline === true,
    leadMinutes: isValidLeadMinutes(r.compute_deadline_lead_minutes) ? r.compute_deadline_lead_minutes : DEFAULT_LEAD_MINUTES,
  }
}

/**
 * The lead time the in-app notice uses.
 *
 * ⚠ The SAVED lead time, whether the email is on or off: the switch controls only the email
 * (Benoit, 2026-09-15, https://github.com/nobgp/app/issues/354#issuecomment-5687997223). When no
 * preference can be read (before 0199 is applied, or the read failed), the notice uses 60 minutes.
 */
export const noticeLeadMinutes = (available: boolean, preference: DeadlinePreference): number =>
  available && isValidLeadMinutes(preference.leadMinutes) ? preference.leadMinutes : DEFAULT_LEAD_MINUTES

export type LeadTimeOption = { minutes: number; label: string }

/**
 * The lead times offered. 15 is the column's floor, tied to the compute-stop sweeper's ten-minute
 * tick; 120 is the top.
 *
 * ⚠⚠ NOTHING LONGER, AND THE REASON IS THE IN-APP NOTICE AND NOT THE EMAIL. The notice shows for
 * the WHOLE lead window, while the email is one message per task. Every plan's
 * `compute_max_hours_default` is 24 h, so a 24-hour lead put every machine inside its own window
 * from the moment it was provisioned: one undismissable alert per running machine above the usage
 * table and a banner on every node page, for the machine's whole life. router#968 measured 14
 * forgotten containers in one org.
 *
 * ⚠ Dropping the long choices is the fix Benoit chose (2026-09-16) over gating the notice on
 * `deadlineSeverity`, because it needs no second rule: a lead that cannot be longer than two hours
 * cannot swallow a machine's life. The lead only has to cover the gap between the warning arriving
 * and the reader being able to press Give more time.
 *
 * ⚠ A week-long machine therefore gets two hours' notice and not a day. That is the accepted cost;
 * the remedy for a machine you want to keep is a longer deadline, not a longer warning.
 *
 * ⚠ `MAX_LEAD_MINUTES` stays 1440, matching the column CHECK. Only the OFFERED choices shrink —
 * validation must not refuse a value the database accepts, and a saved non-preset is still kept as
 * a choice by `leadTimeOptions` below.
 */
const PRESET_MINUTES = [15, 30, 60, 120] as const

const leadLabel = (minutes: number): string => {
  if (minutes % 60 === 0) {
    const hours = minutes / 60
    return `${hours} hour${hours === 1 ? '' : 's'} before`
  }
  return `${minutes} minutes before`
}

/**
 * The choices of the lead time select.
 *
 * ⚠ A saved value that is not a preset (the column accepts any value 15..1440) is added, so the
 * select can show it. Without it, the select shows an empty value and a save could change it.
 */
export const leadTimeOptions = (current: number): LeadTimeOption[] => {
  const minutes: number[] = [...PRESET_MINUTES]
  if (isValidLeadMinutes(current) && !minutes.includes(current)) minutes.push(current)
  return minutes.sort((a, b) => a - b).map(m => ({ minutes: m, label: leadLabel(m) }))
}

export type DeadlineNotice = {
  kind: 'stops-soon' | 'stopping' | 'stopped-at-limit'
  severity: 'info' | 'warning' | 'error'
  text: string
}

/**
 * The notice for one task, or null when there is none to show.
 *
 * - An open task whose deadline is inside the lead time: `Stops in 42m`. The severity is the one
 *   the deadline bar uses (`deadlineSeverity`), so the two do not disagree.
 * - An open task past its deadline: the sweeper stops it within about ten minutes.
 * - An ended task with `stop_reason = 'deadline'`: `Stopped at its time limit`.
 *
 * ⚠⚠ THE STOPPED NOTICE READS `stop_reason` ONLY, NEVER THE TIMESTAMPS. A task that ended after
 * its deadline was not always stopped by it (a Terminate can land in the sweeper's window). Where
 * the reason is not known (`null` or `undefined`), there is no stopped notice.
 *
 * ⚠ The app has no error boundary, so an invalid date must not throw. A deadline that is not a
 * real instant gives no notice.
 */
export const deadlineNotice = (
  lifecycle: TaskLifecycle,
  stopReason: string | null | undefined,
  leadMinutes: number,
): DeadlineNotice | null => {
  if (lifecycle.state === 'ended') {
    return stopReason === 'deadline'
      ? { kind: 'stopped-at-limit', severity: 'info', text: 'Stopped at its time limit' }
      : null
  }

  const { deadlineAt, remainingMs } = lifecycle
  if (deadlineAt === null || !Number.isFinite(deadlineAt.getTime())) return null
  if (remainingMs === null || !Number.isFinite(remainingMs)) return null

  const lead = isValidLeadMinutes(leadMinutes) ? leadMinutes : DEFAULT_LEAD_MINUTES
  if (remainingMs > lead * 60_000) return null

  if (remainingMs <= 0) {
    return { kind: 'stopping', severity: 'error', text: 'Reached its time limit and stops in a few minutes' }
  }

  const severity = deadlineSeverity(lifecycle)
  return {
    kind: 'stops-soon',
    severity: severity === 'primary' ? 'info' : severity,
    text: `Stops in ${formatDuration(remainingMs)}`,
  }
}
